import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography, Grid, Container } from "@material-ui/core";
import { Avatar, Button, CssBaseline, TextField, InputAdornment, IconButton } from "@material-ui/core";

// Services
import auth from "../../../../services/auth/auth";

// Components
import AlertComponent from "../../../elements/Alert";
import { DotFalling } from "../../../loader/Loaders";

// Connectors
import backendConnector from "../../../../services/connectors/backendConnector";

// Icons
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";

// Styles
import LoginFormStyle from "../../../../assets/styles/materialUI/questionnaire/authentication/login/LoginFormStyle";

// Providers
import { useLanguage } from "../../../../contexts/languageContext";
import { useUserInfoUpdate } from "../../../../contexts/userInfoContext";

const LoginForm = ({ isLoading, setIsLoading, error, setError, message, setMessage }) => {
	const history = useHistory();
	const { t } = useTranslation();
	const formRef = React.useRef();
	const classes = LoginFormStyle();
	const { direction } = useLanguage();
	const updateUser = useUserInfoUpdate();
	const [username, setUsername] = useState(null);
	const [password, setPassword] = useState(null);
	const [showPassword, setShowPassword] = useState(false);

	const authenticate = async () => {
		if (!formRef.current.reportValidity()) return;
		else if (!auth.isActive()) {
			setError(t("auth.login.errors.licenseExpired"));
		} else {
			setIsLoading(true);
			setError(null);
			let credentials = {
				username: username,
				password: password,
			};
			let response = await backendConnector.authenticate(credentials);
			if (response?.access_token) {
				let token = auth.setTokens(response);
				updateUser(token);
				history.push("/questionnaire");
			} else {
				setError(t(`auth.login.errors.invalidUsernameOrPassword`));
			}
			setIsLoading(false);
		}
	};

	const redirectSignUp = () => {
		history.push("/questionnaire/signup");
	};

	const redirectForgotPassword = () => {
		history.push("/questionnaire/forgot_password");
	};

	const handleRedirectForgotPassword = (event) => {
		event.preventDefault();
		redirectForgotPassword();
	};

	return (
		<>
			<Container component="main" maxWidth="xs" dir={direction}>
				<CssBaseline />
				<Grid className={classes.paper}>
					<Avatar className={classes.avatar}>
						<LockOutlinedIcon />
					</Avatar>
					<Typography variant="h6" color="textSecondary" align="center">
						{t("auth.login.title")}
					</Typography>
					<form className={classes.form} ref={formRef}>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							id="username"
							label={t("auth.login.username")}
							name="username"
							value={username}
							onChange={(e) => setUsername(e.target.value.trim())}
							onKeyDown={(e) => {
								if (e.key === "Enter") authenticate();
							}}
						/>
						<TextField
							variant="filled"
							margin="normal"
							required
							fullWidth
							name="password"
							label={t("auth.login.password")}
							type="text"
							autoComplete="current-password"
							id="password"
							value={password}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label={
												showPassword
													? t("common.buttons.hidePassword")
													: t("common.buttons.showPassword")
											}
											onClick={() => setShowPassword((prev) => !prev)}
											onMouseDown={(e) => e.preventDefault()}
											edge="end"
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
							inputProps={{
								style: {
									WebkitTextSecurity: showPassword ? "none" : "disc",
								},
							}}
							onChange={(e) => setPassword(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") authenticate();
							}}
						/>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							onClick={authenticate}
						>
							{t("auth.login.submit")}
						</Button>
					</form>
				</Grid>
				<Box>
					<Typography align="right">
						<a href="#" onClick={handleRedirectForgotPassword}>
							{t("auth.login.forgot")}
						</a>
					</Typography>
				</Box>

				<Box mt={1} className={classes.normalSeperator}>
					<Typography variant="body2" color="textSecondary" align="center">
						{t("auth.login.or")}
					</Typography>
				</Box>
				<Box mt={1} className={classes.smallSeperator}>
					<Typography variant="body2" color="textSecondary" align="center">
						{t("auth.login.or")}
					</Typography>
				</Box>
				<Button
					fullWidth
					variant="contained"
					color="secondary"
					className={classes.submit}
					onClick={redirectSignUp}
				>
					{t("auth.login.create")}
				</Button>
				<Grid className={`${classes.loaderContainer} h-100 w-100 d-flex justify-content-center`}>
					<Grid className={classes.loader}>{isLoading && <DotFalling />}</Grid>
				</Grid>
			</Container>
			{(error || message) && (
				<AlertComponent
					errors={error ? [error] : []}
					messages={message ? [message] : []}
					setError={setError}
					setMessage={setMessage}
				/>
			)}
		</>
	);
};

export default LoginForm;
